import { useGetInvoice, getGetInvoiceQueryKey } from "@workspace/api-client-react";
import type { InvoiceTemplateConfig } from "@workspace/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * D-07 HTML-first preview of invoice. Renders the SAME data shape that the
 * PDF generator uses (artifacts/api-server/src/lib/invoice-renderer.ts → InvoiceForPdf).
 * Operator clicks "Preview" in the invoice-queue or today's-billing card → this component
 * renders inside a Dialog. The PDF is the artifact attached to delivery (D-08); the HTML
 * is the operator-facing review surface BEFORE Send.
 *
 * Single source of truth: GET /billing/invoices/:id returns InvoiceWithLineItems which
 * has the same fields the PDF needs. Both this component and the server-side
 * generateInvoicePdf() call the same data-shape function (Plan 03-04 invoice-renderer.ts
 * factors out getInvoiceData(invoiceId) for reuse).
 *
 * RESEARCH Pattern 7 option 1 — optional templateConfigOverride + documentType for live preview.
 */
interface Props {
  /** When provided, fetch invoice via useGetInvoice */
  invoiceId?: number;
  /** When provided, use this data directly (synthetic preview — no fetch) */
  invoiceData?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Override template config for live preview in /settings/invoice-template */
  templateConfigOverride?: InvoiceTemplateConfig | null;
  /** 'Invoice' | 'Receipt'; default from invoice data or 'Invoice' */
  documentType?: "Invoice" | "Receipt";
}

export function InvoiceHtmlPreview({
  invoiceId,
  invoiceData,
  templateConfigOverride,
  documentType: documentTypeProp,
}: Props) {
  // Existing fetch (kept for backward compat when invoiceId is passed)
  const { data: fetched, isLoading } = useGetInvoice(invoiceId ?? 0, {
    query: {
      enabled: invoiceId != null,
      queryKey: invoiceId ? getGetInvoiceQueryKey(invoiceId) : ["nope"],
    },
  });

  const inv = invoiceData ?? fetched;
  const docType: "Invoice" | "Receipt" =
    documentTypeProp ??
    ((inv?.documentType as "Invoice" | "Receipt" | undefined) ?? "Invoice");

  // Template config overrides (D-07 + D-10)
  const tc = templateConfigOverride ?? null;

  if (invoiceId != null && isLoading) return <Skeleton className="h-96 w-full" />;
  if (!inv) return <div className="text-sm text-muted-foreground p-4">Invoice not found.</div>;

  const subtotal = Number(inv.subtotal);
  const cgstTotal = Number(inv.cgstTotal ?? 0);
  const sgstTotal = Number(inv.sgstTotal ?? 0);
  const igstTotal = Number(inv.igstTotal ?? 0);
  const total = Number(inv.total);
  const amountPaid = Number(inv.amountPaid);
  const amountDue = total - amountPaid;

  // Warning 10 — 5 required inserts:
  // 1. bandClass derivation — 4px top border per UI-SPEC Surface 8 / Receipt vs Invoice
  const bandClass = docType === "Receipt" ? "border-t-4 border-amber-400" : "border-t-4 border-primary";
  // 2. headerLabel — "RECEIPT" or "TAX INVOICE"
  const headerLabel = docType === "Receipt" ? "RECEIPT" : "TAX INVOICE";
  const headerLabelColor = docType === "Receipt" ? "text-amber-700" : "";

  // GSTIN display — override from tc or fall through to inv.issuerGstin
  const displayGstin = tc?.gstin ?? inv.issuerGstin;
  const footer = tc?.footer ?? null;

  return (
    <div
      className={`bg-white text-black p-6 font-sans text-sm ${bandClass}`}
      id="invoice-html-preview"
    >
      {/* 3. tc?.logoUrl — logo conditional insert */}
      {tc?.logoUrl && (
        <img src={tc.logoUrl} alt="logo" className="h-20 mb-2 object-contain" />
      )}

      {/* Header — issuer + document type label */}
      <div className="flex justify-between items-start mb-4 pb-3 border-b">
        <div>
          <div className="text-lg font-bold">{tc?.issuerName || "Hoblix Coworking"}</div>
          {tc?.registeredAddress && (
            <div className="text-xs whitespace-pre-line">{tc.registeredAddress}</div>
          )}
          {tc?.contactInfo && (
            <div className="text-xs">{tc.contactInfo}</div>
          )}
          {/* GSTIN — override from tc?.gstin or invoice's issuerGstin (also identifies state) */}
          {displayGstin ? (
            <div className="text-xs">GSTIN: <span className="font-mono">{displayGstin}</span></div>
          ) : null}
        </div>
        <div className="text-right">
          {/* 2. headerLabel — "TAX INVOICE" or "RECEIPT" */}
          <div className={`text-lg font-bold ${headerLabelColor}`}>{headerLabel}</div>
          <div className="font-mono text-base mt-1">#{inv.invoiceNumber}</div>
          <div className="text-xs">Date: {inv.invoiceDate}</div>
        </div>
      </div>

      {/* Bill To — must mirror the PDF template (member-invoice.tsx): for a firm
          (company/team account) show the primary contact on top and address the
          firm as "M/s {name}"; individuals show just the name. Falls back to the
          account code when no name is on file. */}
      <div className="mb-4">
        <div className="text-xs uppercase font-semibold mb-1">Bill To:</div>
        {inv.memberIsCompany && inv.memberContactName ? (
          <div>{inv.memberContactName}</div>
        ) : null}
        <div>
          {inv.memberName
            ? inv.memberIsCompany
              ? `M/s ${inv.memberName}`
              : inv.memberName
            : `MEM-${String(inv.memberId).padStart(4, "0")}`}
        </div>
        {inv.memberAddress ? <div className="text-xs">{inv.memberAddress}</div> : null}
        {inv.memberGstin ? (
          <div className="text-xs">GSTIN: <span className="font-mono">{inv.memberGstin}</span></div>
        ) : null}
        {inv.memberMobile ? <div>{inv.memberMobile}</div> : null}
      </div>

      {/* Line items table */}
      <table className="w-full border-collapse mb-4">
        <thead>
          <tr className="border-b text-xs uppercase">
            <th className="text-left py-2 pr-2">Description</th>
            <th className="text-left py-2 pr-2">HSN/SAC</th>
            <th className="text-right py-2 pr-2">Qty</th>
            <th className="text-right py-2 pr-2">Rate</th>
            <th className="text-right py-2 pr-2">Taxable</th>
            <th className="text-right py-2 pr-2">GST</th>
            <th className="text-right py-2">Line Total</th>
          </tr>
        </thead>
        <tbody>
          {(inv.lineItems ?? []).map((li: any, i: number) => ( // eslint-disable-line @typescript-eslint/no-explicit-any
            <tr key={i} className="border-b border-zinc-100">
              <td className="py-2 pr-2">{li.description}</td>
              <td className="py-2 pr-2 text-xs">{li.hsnSacCode ?? "—"}</td>
              <td className="py-2 pr-2 text-right font-mono">{li.quantity}</td>
              <td className="py-2 pr-2 text-right font-mono">Rs. {Number(li.unitPrice).toFixed(2)}</td>
              <td className="py-2 pr-2 text-right font-mono">Rs. {Number(li.taxableAmount).toFixed(2)}</td>
              <td className="py-2 pr-2 text-right font-mono">{Number(li.gstRate)}%</td>
              <td className="py-2 text-right font-mono">Rs. {Number(li.lineTotal).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 4. tc?.notes — operator notes rendered between line items and totals */}
      {tc?.notes && (
        <div className="mt-2 text-xs italic text-zinc-600">{tc.notes}</div>
      )}

      {/* Totals block */}
      <div className="ml-auto w-72 space-y-1 text-sm">
        <div className="flex justify-between"><span>Subtotal (excl. GST):</span><span className="font-mono">Rs. {subtotal.toFixed(2)}</span></div>
        {cgstTotal > 0 ? <div className="flex justify-between"><span>CGST:</span><span className="font-mono">Rs. {cgstTotal.toFixed(2)}</span></div> : null}
        {sgstTotal > 0 ? <div className="flex justify-between"><span>SGST:</span><span className="font-mono">Rs. {sgstTotal.toFixed(2)}</span></div> : null}
        {igstTotal > 0 ? <div className="flex justify-between"><span>IGST:</span><span className="font-mono">Rs. {igstTotal.toFixed(2)}</span></div> : null}
        <div className="flex justify-between border-t pt-1 mt-1">
          <span className="font-bold">TOTAL:</span>
          <span className="font-mono font-bold text-lg">Rs. {total.toFixed(2)}</span>
        </div>
        <div className="flex justify-between"><span>Amount Paid:</span><span className="font-mono">Rs. {amountPaid.toFixed(2)}</span></div>
        {tc?.paymentMode ? (
          <div className="flex justify-between"><span>Payment Mode:</span><span>{tc.paymentMode}</span></div>
        ) : null}
        {amountDue > 0 ? (
          <div className="flex justify-between text-red-600">
            <span>Amount Due:</span><span className="font-mono">Rs. {amountDue.toFixed(2)}</span>
          </div>
        ) : null}
      </div>

      {/* Shared numbered commercial terms (also shown on the receipt) */}
      {tc?.terms && tc.terms.length > 0 ? (
        <div className="mt-5 border-t pt-3 text-xs">
          <div className="mb-1 font-bold text-zinc-600">Terms &amp; Conditions:</div>
          <ol className="list-decimal space-y-0.5 pl-5 text-zinc-600">
            {tc.terms.map((t: string, i: number) => (
              <li key={i}>{t}</li>
            ))}
          </ol>
        </div>
      ) : null}

      {/* 5. tc?.footer — footer override; falls back to hardcoded text */}
      {tc?.footer ? (
        <div className="mt-4 border-t pt-2 text-xs text-zinc-500">{tc.footer}</div>
      ) : (
        <>
          <div className="text-xs text-zinc-500 mt-6 pt-3 border-t">
            This is a computer-generated invoice and does not require a physical signature.
          </div>
          <div className="text-xs text-zinc-500">
            For queries, contact your space admin.
          </div>
        </>
      )}
    </div>
  );
}
